import React, { useEffect, useState } from "react";
import { putApi } from "../../../api/api_instance";
import { API_URLS } from "../../../api/apiUrls";
import PopupCard from "../../../components/Cards/PopupCard/PopupCard";
import { Check } from "lucide-react";

const ResetPassword = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [errors, setErrors] = useState({});
  const [popupData, setPopupData] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumbers: false,
    hasSpecialChar: false,
  });

  useEffect(() => {
    validatePasswordStrength(newPassword);
  }, [newPassword]);

  const validatePasswordStrength = (password) => {
    setPasswordStrength({
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumbers: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  const resetFields = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
    setErrors({});
    setPasswordStrength({
      minLength: false,
      hasUpperCase: false,
      hasLowerCase: false,
      hasNumbers: false,
      hasSpecialChar: false,
    });
  };

  const validate = () => {
    const newErrors = {};

    if (!currentPassword) {
      newErrors.currentPassword = "Current password is required.";
    }

    if (!newPassword) {
      newErrors.newPassword = "New password is required.";
    } else {
      if (!passwordStrength.minLength) {
        newErrors.newPassword = "Password must be at least 8 characters.";
      }
      if (!passwordStrength.hasUpperCase) {
        newErrors.newPassword = "Password must include an uppercase letter.";
      }
      if (!passwordStrength.hasLowerCase) {
        newErrors.newPassword = "Password must include a lowercase letter.";
      }
      if (!passwordStrength.hasNumbers) {
        newErrors.newPassword = "Password must include a number.";
      }
      if (!passwordStrength.hasSpecialChar) {
        newErrors.newPassword = "Password must include a special character.";
      }
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Confirm password is required.";
    } else if (confirmPassword !== newPassword) {
      newErrors.confirmPassword = "Confirm password does not match.";
    }

    return newErrors;
  };

  const showPopup = (type, title, message) => {
    setPopupData({ type, title, message });
    setTimeout(() => setPopupData(null), 3000); 
  };

  const saveChanges = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});

    try {
      const response = await putApi({
        apiUrl: API_URLS.RESET_PASSWORD,
        data: {
          currentPassword: currentPassword,
          newPassword: newPassword,
        },
      });
      if (response.success) {
        showPopup(
          "success",
          "Password Changed",
          response.message || "Your password has been successfully updated."
        );
        resetFields();
      } else {
        showPopup(
          "error",
          "Change Failed",
          response.message || "Something went wrong."
        );
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      if (error.message === "Request failed with status code 400") {
        const errorMessage = "Current password is incorrect";
        showPopup("error", "Change Failed", errorMessage);
        return;
      } else {
        const errorMessage =
          error?.message ||
          "Unable to change password (Check Current password)";

        showPopup("error", "Change Failed", errorMessage);
      }
    }
    // TODO: Submit to backend
  };

  return (
    <div className="max-w-4xl mx-auto rounded-lg">
      <p
        className="text-xl font-semibold mb-6 text-[#06513D]"
        style={{ opacity: 0.99 }}
      >
        Change Your Passwords
      </p>

      {/* Current Password */}
      <div className="mb-5">
        <label
          className="block text-sm font-medium mb-1"
          style={{ opacity: 0.99 }}
        >
          Type Your Current Password
        </label>
        <input
          type={showCurrent ? "text" : "password"}
          className={`w-full h-10 border rounded px-3 ${
            errors.currentPassword ? "border-red-500" : "border-[#06513D]"
          }`}
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
        />
        {errors.currentPassword && (
          <p className="text-red-500 text-sm mt-1">{errors.currentPassword}</p>
        )}
        <label className="inline-flex items-center text-sm mt-2">
          <input
            type="checkbox"
            className="mr-2"
            checked={showCurrent}
            onChange={() => setShowCurrent(!showCurrent)}
          />
          Show Password
        </label>
      </div>

      {/* New Password */}
      <div className="mb-5">
        <label
          className="block text-sm font-medium mb-1"
          style={{ opacity: 0.99 }}
        >
          Type Your New Password
        </label>
        <input
          type={showNew ? "text" : "password"}
          className={`w-full h-10 border rounded px-3 ${
            errors.newPassword ? "border-red-500" : "border-[#06513D]"
          }`}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
        {errors.newPassword && (
          <p className="text-red-500 text-sm mt-1">{errors.newPassword}</p>
        )}
        <label className="inline-flex items-center text-sm mt-2">
          <input
            type="checkbox"
            className="mr-2"
            checked={showNew}
            onChange={() => setShowNew(!showNew)}
          />
          Show Password
        </label>
        {newPassword && (
          <div className="mt-2 space-y-1">
            {[
              {
                label: "At least 8 characters",
                valid: passwordStrength.minLength,
              },
              {
                label: "One uppercase letter",
                valid: passwordStrength.hasUpperCase,
              },
              {
                label: "One lowercase letter",
                valid: passwordStrength.hasLowerCase,
              },
              {
                label: "One number",
                valid: passwordStrength.hasNumbers,
              },
              {
                label: "One special character (@, #, $, etc.)",
                valid: passwordStrength.hasSpecialChar,
              },
            ].map((item, i) => (
              <div key={i} className="flex items-center text-[0.6rem]">
                <div
                  className={`w-2 h-2 rounded-full mr-2 flex items-center justify-center ${
                    item.valid ? "bg-[#06513D]" : "bg-gray-300"
                  }`}
                >
                  {item.valid && <Check className="w-2 h-2 text-white" />}
                </div>
                <span
                  className={item.valid ? "text-[#06513D]" : "text-gray-500"}
                >
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirm Password */}
      <div className="mb-5">
        <label
          className="block text-sm font-medium mb-1"
          style={{ opacity: 0.99 }}
        >
          Confirm Your New Password
        </label>
        <input
          type={showConfirm ? "text" : "password"}
          className={`w-full h-10 border rounded px-3 ${
            errors.confirmPassword ? "border-red-500" : "border-[#06513D]"
          }`}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
        {errors.confirmPassword && (
          <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
        )}
        <label className="inline-flex items-center text-sm mt-2">
          <input
            type="checkbox"
            className="mr-2"
            checked={showConfirm}
            onChange={() => setShowConfirm(!showConfirm)}
          />
          Show Password
        </label>
      </div>

      {/* Buttons */}
      <div className="flex gap-4 mt-8">
        <button
          onClick={resetFields}
          className="w-[150px] h-[48px] border border-[#06513D] rounded-[10px] px-5 py-2 text-[#06513D] bg-[#E5F3F6]"
        >
          Reset
        </button>
        <button
          onClick={saveChanges}
          className="w-[150px] h-[48px] bg-[#06513D] text-white rounded-[10px] px-5 py-2"
        >
          Save
        </button>
      </div>

      {/* PopupCard */}
      {popupData && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
          <PopupCard
            type={popupData.type}
            title={popupData.title}
            message={popupData.message}
          />
        </div>
      )}
    </div>
  );
};

export default ResetPassword;
